import {
  activeScheduleOf,
  addDays,
  computeInsights,
  countdownLabel,
  diffDays,
  formatDate,
  greeting,
  nextWeekDate,
  relativeTime,
  sessionOn,
  sessionPhase,
  sortByDate,
  thisWeekDate,
  waitingMembers,
  type Session,
} from "@love-cpmd/core";
import { Badge, Button, buttonClasses, Card, cn, SectionHeader } from "@love-cpmd/ui";
import {
  ArrowLeftRight,
  ArrowRight,
  BellRing,
  CalendarPlus,
  ChartColumnBig,
  ChevronRight,
  CircleCheck,
  Clock3,
  Eye,
  History,
  Library,
  Link2,
  MapPin,
  NotebookPen,
  PencilLine,
  SkipForward,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorState, InstallHint, PageContainer, PageSkeleton } from "../components/common/layout-bits";
import { CategoryChip, CategoryDot, CountdownBadge, PresenterAvatar, presenterMeta, SessionArt, SessionBadges } from "../components/common/session-bits";
import { useAccess } from "../lib/auth";
import { useOverlay } from "../lib/hooks";
import { useWorld, type World } from "../lib/queries";

export function DashboardPage() {
  const { world, isLoading, isError, refetch } = useWorld();
  const { user, show } = useAccess();
  if (isError) return <ErrorState onRetry={refetch} />;
  if (isLoading || !world || !user) return <PageSkeleton />;

  const weekday = world.settings.weekday;
  const thisWeek = sessionOn(world.sessions, thisWeekDate(world.today, weekday));
  const nextWeek = sessionOn(world.sessions, nextWeekDate(world.today, weekday));
  // Reminder: the most recent completed session without a summary (pushes Session Documentation Rate).
  const lastDone = sortByDate(world.sessions.filter((s) => s.status === "completed" && s.type !== "spontaneous"), "desc")[0];
  const needsSummary = lastDone && !world.summaryBySession.has(lastDone.id) ? lastDone : undefined;
  const reminder = needsSummary && <SummaryReminder session={needsSummary} world={world} />;

  return (
    <PageContainer>
      <header className="flex flex-col gap-1">
        <p className="text-label-sm font-bold tracking-wider text-primary uppercase">{formatDate(world.today, "full")}</p>
        <h1 className="text-headline-lg text-on-surface">
          {greeting()}, {user.nickname}
        </h1>
      </header>
      <InstallHint />
      {reminder && <div className="hidden lg:block">{reminder}</div>}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <ThisWeekCard session={thisWeek} world={world} className="lg:col-span-2" />
        <div className="flex flex-col gap-5">
          <NextWeekCard session={nextWeek} world={world} />
          {show("dashboard.myTurn") && <MyNextTurnCard world={world} userId={user.id} />}
        </div>
      </div>

      {reminder && <div className="lg:hidden">{reminder}</div>}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <RecentSharingCard world={world} className="lg:col-span-2" />
        <SharingInsightCard world={world} />
      </div>
    </PageContainer>
  );
}

/* ───────── Summary reminder ───────── */

function SummaryReminder({ session, world }: { session: Session; world: World }) {
  const { show, enabled } = useAccess();
  const days = diffDays(world.today, session.date);
  return (
    <div className="relative flex flex-col gap-3 overflow-hidden rounded-card bg-error-container/45 p-4 pl-5 ring-1 ring-error-container sm:flex-row sm:items-center sm:justify-between">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 bg-primary-container" />
      <div className="flex min-w-0 items-center gap-3.5">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-surface-container-lowest text-primary-container shadow-card">
          <BellRing className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="primary" size="xs">
              Summary reminder
            </Badge>
            <span className="text-body-sm text-on-surface-variant">{days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`}</span>
          </div>
          <p className="mt-1 text-label-lg text-on-surface">
            The session on <span className="text-primary">{formatDate(session.date, "dayMonth")}</span> ({world.name(session.presenter, "short")} —{" "}
            {session.topic || "untitled"}) doesn't have a summary or key takeaways yet.
          </p>
        </div>
      </div>
      {show("summary.edit") && (
        <Link
          to={`/summary/${session.id}/edit`}
          aria-disabled={!enabled("summary.edit")}
          className={buttonClasses({ className: "shrink-0 self-start sm:self-auto" })}
        >
          <NotebookPen className="size-[18px]" aria-hidden /> Add summary
        </Link>
      )}
    </div>
  );
}

/* ───────── This week ───────── */

function ThisWeekCard({ session, world, className }: { session?: Session; world: World; className?: string }) {
  const { show, enabled } = useAccess();
  const { openSession, openAction } = useOverlay();
  const date = thisWeekDate(world.today, world.settings.weekday);
  const isToday = date === world.today;

  if (!session) {
    return (
      <Card padding="lg" className={cn("flex flex-col items-start justify-between gap-6 overflow-hidden", className)}>
        <span aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-primary-container to-secondary-container" />
        <Badge tone="brand" size="md">
          {isToday ? "Today" : "This week"}
        </Badge>
        <div className="flex flex-col gap-2">
          <h2 className="text-headline-lg text-on-surface">No presenter yet</h2>
          <p className="text-body-md text-on-surface-variant">Nobody is scheduled for {formatDate(date, "long")}. Pick a presenter so the team can prepare.</p>
        </div>
        {show("schedule.edit") && (
          <Button leading={<CalendarPlus />} onClick={() => openAction("new-schedule", { date })} disabled={!enabled("schedule.edit")}>
            Input schedule
          </Button>
        )}
      </Card>
    );
  }

  const category = session.categoryId ? world.categoriesById.get(session.categoryId) : undefined;
  const phase = sessionPhase(session, world.today);

  return (
    <Card padding="none" className={cn("group flex flex-col overflow-hidden transition-shadow hover:shadow-raised", className)}>
      <span aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-primary-container to-secondary-container" />
      <span aria-hidden className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-primary-fixed/30 blur-3xl" />
      <div className="relative flex flex-1 flex-col gap-5 p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="brand" size="md" className="tracking-wide uppercase">
              <span className="size-2 animate-pulse rounded-full bg-secondary-fixed" aria-hidden />
              {isToday ? "Today" : "This week"}
            </Badge>
            <Badge tone="neutral" size="md" icon={<Clock3 />}>
              {formatDate(session.date, "medium")} · {world.settings.startTime} WIB
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {phase === "upcoming" && <CountdownBadge date={session.date} today={world.today} size="md" />}
            <SessionBadges session={session} size="md" />
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-5 md:grid-cols-12">
          <div className="flex flex-col gap-3 md:col-span-8">
            <CategoryChip category={category} />
            <h2 className="text-headline-lg text-on-surface transition-colors group-hover:text-primary">
              {session.topic || <span className="text-on-surface-variant">Topic not announced yet</span>}
            </h2>
            {!session.topic && <p className="text-body-md text-on-surface-variant">The presenter fills in the topic by D-3 ({formatDate(addDays(session.date, -3), "dayMonth")}).</p>}
            <div className="mt-1 flex items-center gap-3">
              <PresenterAvatar presenter={session.presenter} world={world} size="lg" ring />
              <div className="min-w-0">
                <p className="truncate text-label-lg text-on-surface">{world.name(session.presenter)}</p>
                <p className="truncate text-body-sm text-on-surface-variant">{presenterMeta(session.presenter, world)}</p>
              </div>
            </div>
          </div>
          <SessionArt category={category} label={category?.shortName} className="hidden aspect-square max-h-48 w-full md:col-span-4 md:block" />
        </div>
      </div>
      <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/30 bg-surface-container-low/70 px-5 py-3 md:px-6">
        <span className="flex min-w-0 items-center gap-2 text-body-sm text-on-surface-variant">
          <MapPin className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{world.settings.location}</span>
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {phase === "today" && show("schedule.edit") ? (
            <Button size="sm" leading={<CircleCheck />} onClick={() => openAction("mark-done", { session: session.id })} disabled={!enabled("schedule.edit")}>
              Mark as done
            </Button>
          ) : (
            phase === "upcoming" &&
            show("schedule.edit") && (
              <Button size="sm" variant="secondary" leading={<ArrowLeftRight />} onClick={() => openAction("change", { session: session.id })} disabled={!enabled("schedule.edit")}>
                Change schedule
              </Button>
            )
          )}
          <Button size="sm" variant={phase === "today" ? "secondary" : "primary"} trailing={<ArrowRight />} onClick={() => openSession(session.id)}>
            Session details
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ───────── Next week ───────── */

function NextWeekCard({ session, world }: { session?: Session; world: World }) {
  const { show, enabled } = useAccess();
  const { openSession, openAction } = useOverlay();
  const date = nextWeekDate(world.today, world.settings.weekday);
  const category = session?.categoryId ? world.categoriesById.get(session.categoryId) : undefined;
  return (
    <Card padding="none" interactive={!!session} className="flex min-h-fit flex-1 flex-col overflow-hidden">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-secondary" />
      <div className="flex flex-1 flex-col gap-2 p-4 pl-5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-label-sm font-bold tracking-wider text-secondary uppercase">
            <SkipForward className="size-4" aria-hidden /> Next week
          </span>
          {session && <SessionBadges session={session} size="xs" />}
        </div>
        {session ? (
          <>
            <CategoryChip category={category} size="xs" />
            <h3 className="line-clamp-2 text-headline-sm text-on-surface">{session.topic || "Topic not announced yet"}</h3>
          </>
        ) : (
          <p className="text-body-md text-on-surface-variant">No presenter yet for {formatDate(date, "dayMonth")}.</p>
        )}
      </div>
      {session ? (
        <button
          type="button"
          onClick={() => openSession(session.id)}
          className="flex items-center justify-between gap-2 border-t border-outline-variant/30 bg-surface-container-low px-4 py-2.5 pl-5 text-left hover:bg-surface-container"
        >
          <span className="flex min-w-0 items-center gap-2">
            <PresenterAvatar presenter={session.presenter} world={world} size="sm" />
            <span className="min-w-0">
              <span className="block truncate text-label-md text-on-surface">{world.name(session.presenter)}</span>
              <span className="block text-body-sm text-on-surface-variant">{formatDate(session.date, "medium")}</span>
            </span>
          </span>
          <ChevronRight className="size-[18px] text-on-surface-variant" aria-label="Open details" />
        </button>
      ) : (
        show("schedule.edit") && (
          <div className="border-t border-outline-variant/30 bg-surface-container-low px-4 py-2.5 pl-5">
            <Button size="sm" variant="soft" leading={<CalendarPlus />} onClick={() => openAction("new-schedule", { date })} disabled={!enabled("schedule.edit")}>
              Input schedule
            </Button>
          </div>
        )
      )}
    </Card>
  );
}

/* ───────── My next turn ───────── */

function MyNextTurnCard({ world, userId }: { world: World; userId: string }) {
  const navigate = useNavigate();
  const { openAction } = useOverlay();
  const { enabled } = useAccess();
  const mine = activeScheduleOf(world.sessions, userId, world.today);
  const queueIndex = world.rotationRows ? waitingMembers(world.rotationRows).findIndex((r) => r.memberId === userId) : -1;
  const category = mine?.categoryId ? world.categoriesById.get(mine.categoryId) : undefined;

  return (
    <Card
      padding="none"
      interactive
      className="flex min-h-fit flex-1 cursor-pointer flex-col overflow-hidden bg-gradient-to-br from-surface-container-lowest to-surface-container-low"
      onClick={() => navigate("/my-schedule")}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate("/my-schedule")}
      aria-label="My next turn — open My Schedule"
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-primary" />
      <div className="flex flex-1 flex-col gap-2 p-4 pl-5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-label-sm font-bold tracking-wider text-primary uppercase">
            <span className="size-2 animate-pulse rounded-full bg-primary" aria-hidden /> My next turn
          </span>
          {mine && (
            <Badge tone="primary" size="xs">
              {countdownLabel(mine.date, world.today)}
            </Badge>
          )}
        </div>
        {mine ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-label-lg text-on-surface">{formatDate(mine.date, "long")}</span>
              {category && <CategoryChip category={category} size="xs" short />}
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-surface-container/70 p-2.5">
              <PencilLine className="size-4 shrink-0 text-outline" aria-hidden />
              <span className="min-w-0">
                <span className="block truncate text-label-md text-on-surface">{mine.topic || "Not filled yet"}</span>
                {!mine.topic && <span className="block text-[11px] text-on-surface-variant">Can stay empty until D-3</span>}
              </span>
            </div>
          </>
        ) : (
          <p className="text-body-md text-on-surface-variant">
            You don't have a schedule yet.
            {queueIndex >= 0 && <> You're <strong className="text-on-surface">#{queueIndex + 1}</strong> in the rotation queue.</>}
          </p>
        )}
      </div>
      {mine && (
        <div className="flex items-center justify-between gap-2 px-4 pb-4 pl-5">
          <span className="text-label-sm text-on-surface-variant">Presenter: You</span>
          <Button
            size="sm"
            variant="tonal"
            leading={<ArrowLeftRight />}
            disabled={!enabled("schedule.edit")}
            onClick={(e) => {
              e.stopPropagation();
              openAction("change", { session: mine.id });
            }}
          >
            Change schedule
          </Button>
        </div>
      )}
    </Card>
  );
}

/* ───────── Recent sharing / What did I miss? ───────── */

function RecentSharingCard({ world, className }: { world: World; className?: string }) {
  const navigate = useNavigate();
  const items = sortByDate(world.sessions.filter((s) => s.status === "completed" && world.summaryBySession.has(s.id)), "desc").slice(0, 3);
  const [first, ...rest] = items;
  const latestUpdate = world.summaries.reduce<string | undefined>((max, s) => (!max || s.updatedAt > max ? s.updatedAt : max), undefined);

  return (
    <Card padding="lg" className={cn("flex flex-col gap-4", className)}>
      <SectionHeader
        icon={<Library />}
        title="Recent sharing & takeaways"
        description="Summaries from the latest sessions"
        action={
          <Link to="/summary" className="inline-flex items-center gap-1 text-label-md text-primary hover:gap-1.5">
            See all <ArrowRight className="size-4" aria-hidden />
          </Link>
        }
      />
      {!first ? (
        <p className="rounded-2xl bg-surface-container-low px-4 py-6 text-center text-body-md text-on-surface-variant">No summaries yet. They appear here after the first session is documented.</p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => navigate(`/summary/${first.id}`)}
            className="group flex flex-col gap-2 rounded-2xl bg-surface-container-low p-4 text-left transition-colors hover:bg-surface-container"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="brand" size="sm" icon={<History />}>
                  What did I miss?
                </Badge>
                <span className="text-label-sm text-on-surface-variant">
                  {formatDate(first.date, "short")} · {world.name(first.presenter)}
                </span>
              </div>
              <CategoryChip category={first.categoryId ? world.categoriesById.get(first.categoryId) : undefined} size="xs" short />
            </div>
            <h3 className="text-headline-sm text-on-surface group-hover:text-primary">{first.topic}</h3>
            <p className="line-clamp-2 text-body-md text-on-surface-variant">{world.summaryBySession.get(first.id)?.summary}</p>
            <div className="flex items-center justify-between pt-1 text-label-sm text-on-surface-variant">
              <span className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Eye className="size-3.5" aria-hidden /> {world.summaryBySession.get(first.id)?.views ?? 0} readers
                </span>
                <span className="inline-flex items-center gap-1">
                  <Sparkles className="size-3.5" aria-hidden /> {world.summaryBySession.get(first.id)?.keyTakeaways.length ?? 0} takeaways
                </span>
                {(world.summaryBySession.get(first.id)?.references.length ?? 0) > 0 && (
                  <span className="hidden items-center gap-1 sm:inline-flex">
                    <Link2 className="size-3.5" aria-hidden /> {world.summaryBySession.get(first.id)?.references.length} links
                  </span>
                )}
              </span>
              <span className="inline-flex items-center gap-0.5 font-bold text-primary">
                Read summary <ArrowRight className="size-3.5" aria-hidden />
              </span>
            </div>
          </button>
          <ul className="flex flex-col gap-1">
            {rest.map((s) => {
              const category = s.categoryId ? world.categoriesById.get(s.categoryId) : undefined;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/summary/${s.id}`)}
                    className="group flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left hover:bg-surface-container-low"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant group-hover:bg-primary-fixed group-hover:text-primary">
                        <NotebookPen className="size-[18px]" aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-x-2 text-[11px] font-semibold text-on-surface-variant">
                          {formatDate(s.date, "short")} · {world.name(s.presenter)}
                          {category && (
                            <span className="inline-flex items-center gap-1 text-on-surface">
                              <CategoryDot color={category.color} /> {category.shortName}
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-label-lg text-on-surface group-hover:text-primary">{s.topic}</span>
                      </span>
                    </span>
                    <ChevronRight className="size-5 shrink-0 text-outline-variant transition-transform group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
          {latestUpdate && <p className="text-body-sm text-on-surface-variant">Last updated {relativeTime(latestUpdate)}</p>}
        </>
      )}
    </Card>
  );
}

/* ───────── Sharing insight ───────── */

function SharingInsightCard({ world }: { world: World }) {
  const navigate = useNavigate();
  const ins = computeInsights(world.sessions, world.categories, world.today, "year");
  const top3 = ins.distribution.filter((d) => d.count > 0).slice(0, 3);
  const otherPct = Math.max(0, 100 - top3.reduce((a, d) => a + d.pct, 0));
  return (
    <Card padding="lg" interactive className="flex cursor-pointer flex-col gap-4" onClick={() => navigate("/insights")}>
      <SectionHeader
        icon={<ChartColumnBig />}
        title="Sharing insight"
        description="Knowledge shared this year"
        action={
          <Badge tone="neutral" size="xs">
            {world.today.slice(0, 4)} YTD
          </Badge>
        }
      />
      <div className="grid grid-cols-3 gap-2">
        {[
          { value: ins.total, label: "Sessions", color: "text-primary" },
          { value: ins.uniqueTopics, label: "Unique topics", color: "text-secondary" },
          { value: ins.categoriesUsed, label: "Categories", color: "text-on-surface" },
        ].map((m) => (
          <div key={m.label} className="flex flex-col items-center rounded-xl bg-surface-container-low px-2 py-3 text-center">
            <span className={cn("text-headline-lg tabular", m.color)}>{m.value}</span>
            <span className="text-[11px] font-medium text-on-surface-variant">{m.label}</span>
          </div>
        ))}
      </div>
      {ins.top && (
        <div className="flex flex-col gap-2 rounded-2xl bg-surface-container-high/50 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">Top category</span>
            <span className="text-label-sm font-bold text-primary tabular">
              {ins.top.count} sessions ({Math.round(ins.top.pct)}%)
            </span>
          </div>
          <p className="flex items-center gap-1.5 text-label-lg text-on-surface">
            <TrendingUp className="size-[18px] text-primary" aria-hidden /> {ins.top.category.name}
          </p>
          <div
            className="mt-1 flex h-2 w-full gap-[2px] overflow-hidden rounded-full"
            role="img"
            aria-label={top3.map((d) => `${d.category.name} ${Math.round(d.pct)}%`).join(", ")}
          >
            {top3.map((d) => (
              <span key={d.category.id} style={{ width: `${d.pct}%`, backgroundColor: d.category.color }} className="h-full" />
            ))}
            {otherPct > 0 && <span style={{ width: `${otherPct}%` }} className="h-full bg-outline-variant" />}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5 text-[11px] text-on-surface-variant">
            {top3.map((d) => (
              <span key={d.category.id} className="inline-flex items-center gap-1">
                <CategoryDot color={d.category.color} /> {d.category.shortName}
              </span>
            ))}
            {otherPct > 0 && (
              <span className="inline-flex items-center gap-1">
                <CategoryDot color="var(--color-outline-variant)" /> Other
              </span>
            )}
          </div>
        </div>
      )}
      <span className={buttonClasses({ variant: "tonal", block: true, className: "mt-auto text-primary" })}>
        Open Topic Insights <ChartColumnBig className="size-[18px]" aria-hidden />
      </span>
    </Card>
  );
}
